import { useForm } from 'react-hook-form';
import MemberSelect from './member-select';
import {
  createRecyclingConsignmentOrderV2Schema,
  CreateRecyclingConsignmentOrderV2SchemaType,
  FORM_KEY,
  PRODUCT_REMARK_DEFAULT_VALUES,
} from './common';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Input, message, Modal } from 'antd';
import LogisticsInput from './logistics-input';
import { useAppSelector } from 'store/hooks';
import { selectUserInfo } from 'store/slices/userInfoSlice';
import BaseInfo from './base-info';
import { OMS_RECYCLE_ORDER_TYPE_MAP } from 'commons/options';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ProductList from './product-list';
import { generateInitSelectedRankDescOptions } from 'pages/product/create-page-v2/common';
import styles from './index.module.scss';
import classNames from 'classnames';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import PaymentInfo from './payment-info';
import i18n from 'i18n';
import LOCALS from 'commons/locals';
import { useToggle } from 'react-use';
import omsRecycleOrderDraftApi from 'apis/oms_recycle_order_draft';
import DraftList from './draft-list';
import SubmitConfirm from './submit-confirm';
import { MEMBER_PAYMENT_ACCOUNT_TYPE_MAP } from 'constants/RecyclingConsignment';
import { getBagTypeNameFromCategoryIdList } from './product-list/form-items/authentication-config';
import type { AiIdentifyPicsRef } from './product-list/form-items/ai-identify-pics';
import { uniq } from 'lodash-es';
import { getExpandedProductSummary, TWILLY_MAX_QUANTITY } from './twilly';
import { normalizeRecyclingConsignmentDraftData } from './draft-normalization';
import { getShopConfig, SHOP_MAP } from 'constant/shop';

const RecyclingConsignmentOrderForm = ({
  staffName,
  storeId,
  storeIdList,
}: {
  staffName: string;
  storeId: number | undefined;
  storeIdList: number[];
}) => {
  const { collapsed, productCategoryCascaderOptions } =
    useAppSelector(selectGlobalInfo);
  const [draftSaveOpen, toggleDraftSaveOpen] = useToggle(false);
  const [draftSaveTitle, setDraftSaveTitle] = useState('');
  const [draftSaveLoading, toggleDraftSaveLoading] = useToggle(false);
  const aiIdentifyRefs = useRef<Record<string, AiIdentifyPicsRef | null>>({});

  const onAiIdentifyRefChange = useCallback(
    (fieldId: string, ref: AiIdentifyPicsRef | null) => {
      if (!fieldId) return;
      if (!ref) {
        delete aiIdentifyRefs.current[fieldId];
        return;
      }

      aiIdentifyRefs.current[fieldId] = ref;
    },
    [],
  );

  const { control, handleSubmit, setValue, watch } =
    useForm<CreateRecyclingConsignmentOrderV2SchemaType>({
      resolver: zodResolver(createRecyclingConsignmentOrderV2Schema),
      defaultValues: {
        memberSearchValue: '',
        email: '',
        name: '',
        postCode: '',
        postCountryCode: '',
        postPhone: '',
        postCountryCode2: '',
        postPhone2: '',
        country: '',
        city: '',
        detailAddress: '',
        isTradeIn: 0, // 默认非置换
        type: OMS_RECYCLE_ORDER_TYPE_MAP.RECYCLE, // 默认为回收合同单
        settlementType: 2, // 结算类型：1-现金结算 2-财务打款
        memberPaymentAccountType: MEMBER_PAYMENT_ACCOUNT_TYPE_MAP.JP,
        payoutDetail: '',
        staffName,
        receptionistId: undefined,
        storeId: storeId || SHOP_MAP.GINZA,
        supplierType: null,
        taxRegime: null,
        baseShopRemark: '',
        productList: [
          {
            id: Date.now().toString(),
            productCategoryIdList: [],
            brandName: 'Hermes',
            quantity: 1,
            name: {
              en: '',
              ja: '',
              zh_CN: '',
              zh_TW: '',
            },
            rank: '',
            rankDesc: {
              en: '',
              ja: '',
              zh_CN: '',
              zh_TW: '',
            },
            attrSize: {
              en: '',
              ja: '',
              zh_CN: '',
              zh_TW: '',
            },
            selectedRankDescOptions: generateInitSelectedRankDescOptions(),
            color: [],
            colors: '',
            ...PRODUCT_REMARK_DEFAULT_VALUES,
            material: [],
            hardware: [],
            stamp: '',
            fullStamp: '',
            accessory: [],
            firstValuationShopRemark: '',
            guestRemarks: '',
            firstValuationStock: 1,
            firstValuationStockRemark: '',
            productPics: '{"front":"","back":"","interior":"","blindStamp":""}',
            storeReceiptPics: [],
            aiIdentifyPics: [],
            bagTypeName: undefined,
            isFakeBag: undefined,
            fakeBagReason: '',
            initXmAssets: false,
          },
        ],
      },
    });

  const normalizeDraftData = (values: Record<string, any>) => {
    const normalizedValues = normalizeRecyclingConsignmentDraftData(values);
    if (
      !normalizedValues.productList ||
      !Array.isArray(normalizedValues.productList)
    ) {
      return normalizedValues;
    }

    return {
      ...normalizedValues,
      productList: normalizedValues.productList.map(
        (item: Record<string, any>, itemIndex: number) => ({
          ...item,
          id: item.id || `${Date.now()}-${itemIndex}`,
          initXmAssets: item.initXmAssets ?? false,
          quantity:
            Number.isInteger(Number(item.quantity)) && Number(item.quantity) > 0
              ? Math.min(Number(item.quantity), TWILLY_MAX_QUANTITY)
              : 1,
          aiIdentifyPics: [],
          isFakeBag: undefined,
          fakeBagReason: undefined,
        }),
      ),
    };
  };

  const stripAiIdentifyPics = (
    values: CreateRecyclingConsignmentOrderV2SchemaType,
  ) => {
    return {
      ...values,
      productList: values.productList.map((item) => {
        const {
          aiIdentifyPics,
          authenticationBagId,
          isFakeBag,
          fakeBagReason,
          ...rest
        } = item;
        return rest;
      }),
    };
  };

  // 页面加载时，从 localStorage 填充表单
  useEffect(() => {
    const cache = localStorage.getItem(FORM_KEY);
    if (cache) {
      const values = normalizeDraftData(JSON.parse(cache));
      Object.entries(values).forEach(([key, value]) => {
        setValue(
          key as keyof CreateRecyclingConsignmentOrderV2SchemaType,
          value as any,
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue]);

  // 当 productCategoryCascaderOptions 加载完成后，为缺少 bagTypeName 的产品计算并设置
  useEffect(() => {
    if (productCategoryCascaderOptions.length === 0) return;

    const productList = watch('productList');
    productList.forEach((item, index) => {
      // 只在 bagTypeName 为空且有分类时设置
      if (!item.bagTypeName && item.productCategoryIdList?.length >= 2) {
        const bagTypeName = getBagTypeNameFromCategoryIdList(
          item.productCategoryIdList,
          productCategoryCascaderOptions,
        );
        setValue(`productList.${index}.bagTypeName`, bagTypeName);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productCategoryCascaderOptions]);

  // 监听表单变化，存到 localStorage
  const allValues = watch();
  useEffect(() => {
    const cacheValues = stripAiIdentifyPics(allValues);
    localStorage.setItem(FORM_KEY, JSON.stringify(cacheValues));
  }, [allValues]);

  const submitSummary = getExpandedProductSummary(
    allValues.productList || [],
    productCategoryCascaderOptions,
    allValues.type ?? OMS_RECYCLE_ORDER_TYPE_MAP.RECYCLE,
  );

  const [submitData, setSubmitData] =
    useState<CreateRecyclingConsignmentOrderV2SchemaType | null>(null);
  const onSubmit = (data: CreateRecyclingConsignmentOrderV2SchemaType) => {
    setSubmitData(data);
  };

  return (
    <div>
      <SubmitConfirm
        data={submitData}
        totalCostPrice={submitSummary.totalCostPrice}
        totalProductCount={submitSummary.productCount}
        onCancel={() => setSubmitData(null)}
        aiIdentifyRefs={aiIdentifyRefs}
      />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <BaseInfo control={control} setValue={setValue} />
        <MemberSelect
          control={control}
          setValue={setValue}
          initCreateSource={storeId}
        />
        <LogisticsInput control={control} setValue={setValue} />
        <PaymentInfo control={control} setValue={setValue} />
        <ProductList
          control={control}
          setValue={setValue}
          onAiIdentifyRefChange={onAiIdentifyRefChange}
        />

        <div className="h-20"></div>

        <Modal
          open={draftSaveOpen}
          onCancel={toggleDraftSaveOpen}
          title={i18n.t(LOCALS.rluYnqBHGi)}
          confirmLoading={draftSaveLoading}
          onOk={async () => {
            toggleDraftSaveLoading();
            if (!draftSaveTitle.trim()) {
              toggleDraftSaveLoading();
              return;
            }

            await omsRecycleOrderDraftApi.create({
              storeId: allValues.storeId,
              data: stripAiIdentifyPics(allValues),
              title: draftSaveTitle.trim(),
            });
            message.success(i18n.t(LOCALS.save_success));
            setTimeout(() => {
              window.location.reload();
            }, 300);
          }}
        >
          <div className="flex gap-2 items-center">
            <div className="required-field shrink-0">
              {i18n.t(LOCALS.title)}
            </div>
            <Input
              value={draftSaveTitle}
              onChange={(e) => setDraftSaveTitle(e.target.value)}
            />
          </div>
        </Modal>

        <div
          className={classNames(
            'flex gap-2 justify-between px-8 fixed bottom-0 right-0 bg-white py-4 shadow-lg border-t border-gray-200',
            collapsed
              ? styles.recyclingConsignmentOrderV2BottomCollapsed
              : styles.recyclingConsignmentOrderV2BottomUnCollapsed,
          )}
        >
          <div>
            <Button
              htmlType="reset"
              danger
              onClick={() => {
                localStorage.removeItem(FORM_KEY);
                window.location.reload();
              }}
            >
              {i18n.t(LOCALS.reset)}
            </Button>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="w-28 text-right">
                {i18n.t(LOCALS.DSLcOTUUkJ)}:
              </span>
              <span>{submitSummary.productCount}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-28 text-right">
                {i18n.t(LOCALS.consignment_total_cost_price)}:
              </span>
              <span>
                {getShopConfig(allValues.storeId).currency}{' '}
                {submitSummary.totalCostPrice.toLocaleString('en-US')}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <DraftList
              storeIdList={storeIdList}
              onUseDraft={(draftData: Record<string, any>) => {
                localStorage.setItem(FORM_KEY, JSON.stringify(draftData));
                window.location.reload();
              }}
            />
            <Button onClick={toggleDraftSaveOpen}>
              {i18n.t(LOCALS.rluYnqBHGi)}
            </Button>
            <Button type="primary" htmlType="submit">
              {i18n.t(LOCALS.submit)}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

const RecyclingConsignmentOrderPageV2 = () => {
  const data = useAppSelector(selectUserInfo);

  const storeIdList = useMemo(() => {
    const { shop, subShop } = data;
    const result: number[] = [];
    if (shop !== undefined) {
      result.push(shop);
    }
    if (subShop && subShop.length > 0) {
      result.push(...subShop);
    }
    return uniq(result);
  }, [data]);

  if (data.isLoading) return null;

  return (
    <RecyclingConsignmentOrderForm
      staffName={data.nickName}
      storeId={data.shop}
      storeIdList={storeIdList}
    />
  );
};

export default RecyclingConsignmentOrderPageV2;
